/**
 * Repository — the only data API screens should touch.
 *
 * Every mutation does the same two things in the same order: write IndexedDB,
 * then queue the outbox. It never awaits the network and never returns a
 * server-shaped result, so a screen cannot accidentally become
 * network-dependent. Sync happens elsewhere, later, maybe.
 *
 * If a write fails, it THROWS (db.js rejects on write failure). Callers on the
 * capture path must surface that — a silently dropped supplier is a lost booth
 * visit, which is the one failure this whole architecture exists to prevent.
 */

import { all, clear, get, put, putAll, del } from './db';
import { META, STORE } from './schema';
import {
  normalizeFair,
  normalizeSupplier,
  newFair,
  newChecklistItem,
  touch,
  tombstone,
} from './entities';
import { DEFAULT_SETTINGS, CHECK_DEFAULT } from './constants';
import { visible } from './merge';
import * as outbox from './outbox';
import * as media from './media';

/* ── meta ───────────────────────────────────────────────────────────────── */

export async function readMeta(key) {
  const row = await get(STORE.META, key);
  return row ? row.v : null;
}

export function writeMeta(key, v) {
  return put(STORE.META, { k: key, v });
}

/* ── Load ───────────────────────────────────────────────────────────────── */

/** Raw load including tombstones — what sync needs. */
export async function getAll() {
  const [fairs, suppliers] = await Promise.all([all(STORE.FAIRS), all(STORE.SUPPLIERS)]);
  return {
    fairs: fairs.map(normalizeFair).filter(Boolean),
    suppliers: suppliers.map(normalizeSupplier).filter(Boolean),
  };
}

/** What the UI loads at boot: tombstones dropped, settings merged over defaults. */
export async function load() {
  const { fairs, suppliers } = await getAll();
  const stored = await readMeta(META.SETTINGS);
  const currentFair = (await readMeta(META.CURRENT_FAIR)) || '';
  const welcomed = (await readMeta(META.WELCOMED)) === true;

  return {
    fairs: visible(fairs).sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)),
    suppliers: visible(suppliers),
    settings: mergeSettings(stored),
    currentFair,
    welcomed,
  };
}

/** Stored settings layer over the defaults, so a new default appears for everyone. */
export function mergeSettings(stored) {
  if (!stored) return { ...DEFAULT_SETTINGS, profile: { ...DEFAULT_SETTINGS.profile } };
  return {
    ...DEFAULT_SETTINGS,
    ...stored,
    profile: { ...DEFAULT_SETTINGS.profile, ...(stored.profile || {}) },
    outreach: { ...DEFAULT_SETTINGS.outreach, ...(stored.outreach || {}) },
    cardQuestions:
      stored.cardQuestions && stored.cardQuestions.length
        ? stored.cardQuestions
        : DEFAULT_SETTINGS.cardQuestions,
  };
}

/* ── Fairs ──────────────────────────────────────────────────────────────── */

export async function saveFair(fair) {
  const next = touch(fair);
  await put(STORE.FAIRS, next);
  await outbox.enqueue({ entity: outbox.ENTITY.FAIR, id: next.id, payload: next });
  return next;
}

/**
 * Delete a fair AND its suppliers (spec § 5.2 — the confirm says so).
 * One tombstone each, so the deletion propagates rather than being undone by
 * the next pull.
 */
export async function deleteFair(fairId) {
  const fair = await get(STORE.FAIRS, fairId);
  const suppliers = (await all(STORE.SUPPLIERS)).filter((s) => s.fairId === fairId);

  if (fair) {
    const dead = tombstone(fair);
    await put(STORE.FAIRS, dead);
    await outbox.enqueue({
      entity: outbox.ENTITY.FAIR,
      id: fairId,
      op: outbox.OP.DELETE,
      payload: dead,
    });
  }
  for (const s of suppliers) {
    const dead = tombstone(s);
    await put(STORE.SUPPLIERS, dead);
    await outbox.enqueue({
      entity: outbox.ENTITY.SUPPLIER,
      id: s.id,
      op: outbox.OP.DELETE,
      payload: dead,
    });
  }
  return { suppliers: suppliers.length };
}

/** Seed the 15 checklist defaults the first time the checklist is opened. */
export function seedChecklist(fair) {
  if (fair.checklist && fair.checklist.length) return fair;
  return { ...fair, checklist: CHECK_DEFAULT.map(newChecklistItem) };
}

/* ── Suppliers ──────────────────────────────────────────────────────────── */

export async function saveSupplier(supplier) {
  const next = touch(supplier);
  await put(STORE.SUPPLIERS, next);
  await outbox.enqueue({ entity: outbox.ENTITY.SUPPLIER, id: next.id, payload: next });
  return next;
}

export async function deleteSupplier(supplierId) {
  const s = await get(STORE.SUPPLIERS, supplierId);
  if (!s) return null;
  const dead = tombstone(s);
  await put(STORE.SUPPLIERS, dead);
  await outbox.enqueue({
    entity: outbox.ENTITY.SUPPLIER,
    id: supplierId,
    op: outbox.OP.DELETE,
    payload: dead,
  });
  // The media rows go too — they are only ever referenced by this supplier.
  const files = await media.mediaForSupplier(supplierId);
  for (const f of files) await media.deleteMedia(f.id);
  return dead;
}

export const getSupplier = (id) => get(STORE.SUPPLIERS, id).then(normalizeSupplier);

/* ── Settings ───────────────────────────────────────────────────────────── */

export async function saveSettings(settings) {
  const next = { ...settings, updatedAt: Date.now() };
  await writeMeta(META.SETTINGS, next);
  await outbox.enqueue({ entity: outbox.ENTITY.SETTINGS, id: 'settings', payload: next });
  return next;
}

export const setCurrentFair = (id) => writeMeta(META.CURRENT_FAIR, id || '');
export const setWelcomed = () => writeMeta(META.WELCOMED, true);

/* ── Sync write-back ────────────────────────────────────────────────────── */

/**
 * Apply a merge result. Only the records whose winner changed are written, so a
 * sync that pulls nothing new costs nothing.
 *
 * No outbox entries here — these came FROM the server; queueing them would
 * bounce them straight back.
 */
export async function writeMerged({ fairs, suppliers, settings }) {
  if (fairs && fairs.length) await putAll(STORE.FAIRS, fairs);
  if (suppliers && suppliers.length) await putAll(STORE.SUPPLIERS, suppliers);
  if (settings) {
    const current = await readMeta(META.SETTINGS);
    const mine = (current && current.updatedAt) || 0;
    const theirs = settings.updatedAt || 0;
    if (theirs > mine) await writeMeta(META.SETTINGS, settings);
  }
}

/* ── First run ──────────────────────────────────────────────────────────── */

/**
 * Prefill the company card from the BeeNexa account (spec § 1) so the buyer
 * only confirms it. Runs once; after that their edits are authoritative.
 *
 * BeeNexa's User carries only some of these fields, so the rest stay empty by
 * design rather than being guessed at.
 */
export async function prefillProfile(userInfo) {
  if (!userInfo) return null;
  if ((await readMeta(META.PROFILE_PREFILLED)) === true) return null;

  const stored = await readMeta(META.SETTINGS);
  const settings = mergeSettings(stored);
  const name = [userInfo.firstname, userInfo.lastname].filter(Boolean).join(' ').trim();

  const profile = {
    ...settings.profile,
    company: settings.profile.company || userInfo.companyName || '',
    name: settings.profile.name || name,
    email: settings.profile.email || userInfo.username || userInfo.email || '',
    phone: settings.profile.phone || userInfo.phone || '',
  };

  const next = { ...settings, profile };
  await writeMeta(META.SETTINGS, next);
  await writeMeta(META.PROFILE_PREFILLED, true);
  return next;
}

/**
 * Create the first fair (spec § 5.1). Reuses an existing fair of the same name
 * rather than duplicating it — the welcome screen can be re-opened from
 * Settings, and doing so must not create "Canton Fair" twice.
 */
export async function createFirstFair({ name, location, dates }) {
  const existing = (await all(STORE.FAIRS))
    .map(normalizeFair)
    .filter(Boolean)
    .find((f) => !f.deletedAt && f.name.trim().toLowerCase() === String(name).trim().toLowerCase());

  if (existing) {
    await setCurrentFair(existing.id);
    return existing;
  }
  const fair = newFair({ name, location, dates });
  const saved = await saveFair(fair);
  await setCurrentFair(saved.id);
  return saved;
}

/* ── Danger zone (spec § 5.16) ──────────────────────────────────────────── */

export async function deleteAllData() {
  media.releaseAllUrls();
  await Promise.all([
    clear(STORE.FAIRS),
    clear(STORE.SUPPLIERS),
    clear(STORE.MEDIA),
    clear(STORE.OUTBOX),
    clear(STORE.META),
  ]);
}

export async function counts() {
  const [fairs, suppliers, files, queued] = await Promise.all([
    all(STORE.FAIRS),
    all(STORE.SUPPLIERS),
    all(STORE.MEDIA),
    outbox.pendingCount(),
  ]);
  return {
    fairs: visible(fairs).length,
    suppliers: visible(suppliers).length,
    products: visible(suppliers).reduce((n, s) => n + (s.products || []).length, 0),
    media: files.length,
    mediaBytes: files.reduce((n, f) => n + (f.size || 0), 0),
    queued,
  };
}

/** Remove a single meta key — used by "Preview first-run screen". */
export const clearMeta = (key) => del(STORE.META, key);
